<script>
export default {
  name: "CrmPointsCard",
  props: {
    profile: {
      type: Object,
      default: () => ({})
    },
    dataTest: {
      type: String,
      default: undefined
    },
    flatCardPoint: {
      required: false,
      default: false,
      type: Boolean
    }
  },
  computed: {
    memberLevel() {
      return (
        this.profile.member_list.find(
          a => a.id == this.profile.member_level_id
        ) || []
      );
    },
    hasMemberCardImage() {
      return Boolean(this.memberLevel.card_layout_front);
    },
    cardStyle() {
      if (this.flatCardPoint) return;
      if (this.hasMemberCardImage) {
        return {
          backgroundImage: `url("${encodeURI(
            this.memberLevel.card_layout_front
          )}")`,
          color: "var(--font-primary-color)",
          backgroundColor: "unset"
        };
      }

      return {
        background: "var(--primary-color)",
        color: "var(--font-primary-color)"
      };
    },
    totalPoint() {
      return this.numeralFormat(this.profile.total_point || 0);
    },
    expiringPoint() {
      return this.profile.point_expired || 0;
    },
    expiredDate() {
      if (!this.profile.point_expired_date) return null;
      return this.$dateFormat(this.profile.point_expired_date);
    },
    tierName() {
      return (
        this.memberLevel.member_level_language?.[0]?.display ||
        this.memberLevel.name ||
        this.profile.member_level_name ||
        this.profile.tier_name ||
        this.$t("member_tier")
      );
    },
    tierStyle() {
      return {
        background: this.memberLevel.bg_color || "var(--primary-color)",
        color: this.memberLevel.font_color.trim() || "var(--font-primary-color)"
      };
    }
  },
  methods: {
    showqr() {
      this.$router.push("/qr");
    },
    goRedeem() {
      this.$router.push("/reward");
    }
  }
};
</script>

<template>
  <section
    :class="[
      'crm-points-card',
      { 'crm-points-card--image': hasMemberCardImage && !flatCardPoint },
      {
        'crm-point-card__flat': flatCardPoint
      }
    ]"
    :style="cardStyle"
    :data-test="dataTest"
  >
    <!-- Flat variant: keep original layout -->

    <!-- New default variant -->

    <div class="crm-points-card__top" :class="flatCardPoint ? 'mb-2' : ''">
      <div
        class="crm-points-card__points-block"
        @click="$router.push('/point')"
      >
        <div
          class="crm-points-card__point"
          :class="flatCardPoint ? 'd-inline mr-2' : ''"
        >
          {{ totalPoint }}
        </div>
        <div
          class="crm-points-card__points-label"
          :class="flatCardPoint ? 'd-inline' : ''"
        >
          {{ $t("currency_unit") }}
        </div>
      </div>
      <div
        class="crm-points-card__brand"
        @click="$router.push('/member/' + profile.member_level_id)"
      >
        <img
          v-if="!flatCardPoint && $store.state.theme.themeLogoLine"
          :src="$store.state.domain + $store.state.theme.themeLogoLine"
          class="crm-points-card__logo"
        />
        <div class="crm-points-card__tier-pill">
          {{ tierName }}
          <font-awesome-icon icon="chevron-right"></font-awesome-icon>
        </div>
      </div>
    </div>
    <div class="crm-points-card__bottom">
      <button
        v-if="!flatCardPoint"
        type="button"
        class="crm-points-card__redeem"
        @click="goRedeem"
      >
        {{ $t("redeem_profile_button") || "REDEEM" }}
        <font-awesome-icon icon="chevron-right"></font-awesome-icon>
      </button>

      <div
        v-if="expiringPoint > 0"
        class="crm-points-card__expiring"
        :style="{ color: !flatCardPoint && 'var(--font-primary-color)' }"
      >
        {{ numeralFormat(expiringPoint || 0) }} {{ $t("currency_unit") }}
        {{ $t("point_expired") }}
        {{ expiredDate }}
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.crm-points-card {
  position: relative;
  min-height: 180px;
  overflow: hidden;
  padding: 20px 22px;
  border-radius: var(--crm-border-radius);
  background-position: center;
  background-size: 100% 100%;
  box-shadow: var(--crm-box-shadow);
  // background-color: var(--primary-color);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  // color: white;
}

.crm-points-card:not(.crm-points-card--image):not(
    .crm-point-card__flat
  )::before {
  content: "";
  position: absolute;
  top: -40%;
  right: -20%;
  width: 80%;
  height: 160%;
  background: radial-gradient(
    ellipse at center,
    rgba(255, 255, 255, 0.18) 0%,
    rgba(255, 255, 255, 0) 60%
  );
  pointer-events: none;
}

/* ---------- Flat variant (unchanged) ---------- */
.crm-point-card__flat {
  min-height: unset !important;
  padding: 17px 16px;
  border-radius: var(--crm-border-radius);
  justify-content: flex-start;
  border: 1px solid var(--crm-border-color);
}

.crm-point-card__flat .crm-points-card__point {
  color: var(--primary-color);
}
.crm-point-card__flat::after {
  content: "";
  position: absolute;
  right: -42px;
  bottom: -52px;
  width: 160px;
  height: 160px;
  border: 26px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.crm-points-card--image.crm-point-card__flat::after {
  display: none;
}

.crm-points-card__main {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.crm-points-card__caption {
  font-size: var(--font-body-sm);
  opacity: 0.88;
}

.crm-points-card__tier {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 6px;
  border-radius: 999px;
  font-size: var(--font-body);
  font-weight: 600;
  white-space: nowrap;
}

/* ---------- New default variant ---------- */
.crm-points-card__top {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.crm-points-card__points-block {
  cursor: pointer;
}

.crm-points-card__point {
  font-size: var(--font-heading);
  font-weight: 300;

  // letter-spacing: 0.5px;
}

.crm-points-card__points-label {
  // margin-top: 8px;
  font-size: var(--font-body-sm);
  // letter-spacing: 2px;
  text-transform: uppercase;
  opacity: 0.75;
}

.crm-points-card__brand {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
}

.crm-points-card__logo {
  max-height: 28px;
  width: auto;
}

.crm-points-card__tier-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 14px;
  border: 1px solid var(--crm-border-color);
  border-radius: 999px;
  font-size: var(--font-helper);
  // font-weight: 500;
  // letter-spacing: 1px;
  color: var(--crm-link-color);

  text-transform: uppercase;
  white-space: nowrap;
}

.crm-points-card__tier-icon {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  object-fit: cover;
  flex: 0 0 auto;
}

.crm-points-card__bottom {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: auto;
}

.crm-points-card__redeem {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 22px;
  border: 1px solid currentColor;
  border-radius: 999px;
  background: transparent;
  color: inherit;
  font-size: var(--font-helper);
  // font-weight: 500;
  // letter-spacing: 1px;
  text-transform: uppercase;
  cursor: pointer;
}

.crm-points-card__expiring {
  font-size: var(--font-body-sm);
  // letter-spacing: 1px;
  color: var(--crm-link-color);

  opacity: 0.85;
  text-align: right;
}
</style>
